import { IconInfo, Table } from "@powerledger/ui-component-lib";
import { startCase } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Column } from "react-table";
import { Box, Button, Flex } from "theme-ui";

import { HeaderText } from "@/app/components/page-header";
import { SeparatorLayout } from "@/app/components/separator-layout";
import { TableValueWrapper } from "@/app/components/table-value-wrapper";
import { getRetirementReason, getRetirementType } from "@/app/lib/format-commodity-action-data-for-report";
import { getTableTranslation } from "@/app/lib/get-translations-for-components";
import { RegistryCode } from "@/app/types/generated/graphql";

import { getCommonRecTableData } from "../rec-table-column";
import { ActionValues, isRetireValues, RecActionSummaryProps } from "./rec-action-summary.types";

export default function RecActionSummary<T extends "retire" | "export">({
  action,
  data,
  recOptions,
  onProceed,
  onGoBack,
  infoText,
  buttonActionText,
  activeRegistryCode,
  hiddenColumns,
}: Readonly<RecActionSummaryProps<T>>) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isActiveRegistryErcot = activeRegistryCode === RegistryCode.Ercot;

  const tableColumns = useMemo(() => {
    const commonColumns = [...getCommonRecTableData(t, recOptions, isActiveRegistryErcot)] as Column<
      ActionValues["values"]["0"]
    >[];
    commonColumns.pop();
    commonColumns.push({
      Header: t("Quantity to {{action}}", { action: startCase(action) }),
      accessor: "actionVolume",
      Cell: ({ value }: { value: number }) => <TableValueWrapper value={t(value?.toLocaleString()) || ""} />,
      disableSortBy: true,
    });
    return commonColumns;
  }, [t, recOptions, isActiveRegistryErcot, action]);

  return (
    <Box as="main" sx={{ m: 4, position: "relative" }}>
      <HeaderText title={t("{{action}} RECs Confirmation", { action: startCase(action) })} sx={{ width: 350 }} />
      {infoText && (
        <Flex
          sx={{
            fontSize: 0,
            alignItems: "center",
            wordBreak: "break-all",
          }}
        >
          <IconInfo size={5} color="primary.500" />
          {infoText}
        </Flex>
      )}
      <Box sx={{ my: 3 }}>
        <Table
          pagination={false}
          dataSource={data.values}
          columns={tableColumns}
          translation={getTableTranslation(t)}
          initialState={{ hiddenColumns }}
          testId="rec-action-summary-table"
        />
      </Box>
      {isRetireValues(data) && (
        <Box sx={{ mt: 3 }}>
          <SeparatorLayout
            showSeparator={false}
            contentStyle={{
              width: "100%",
            }}
            headingStyle={{
              color: "textDark",
            }}
            descriptionStyle={{
              color: "text",
            }}
            data={{
              "Retirement Type": getRetirementType(data.type, data.category),
              "Retirement Reason": getRetirementReason({
                reason: data.reason ?? "-",
                notes: data.notes ?? "-",
                compliancePeriod: data.compliancePeriod,
              }),
              "Retirement Notes": data.notes,
            }}
          />
        </Box>
      )}
      <Flex
        sx={{
          justifyContent: "flex-end",
          alignItems: "center",
          py: 3,
        }}
      >
        <Flex sx={{ mt: 4, gap: 3, alignItems: "center" }}>
          <Button
            sx={{
              bg: "transparent",
              borderRadius: 0,
              height: 5,
              color: "text",
              "&:enabled:hover": {
                bg: "transparent",
                borderBottom: "1px solid var(--theme-ui-colors-text)",
              },
              "&:enabled:focus": {
                bg: "transparent",
              },
              "&:disabled": {
                bg: "transparent",
              },
            }}
            disabled={isSubmitting}
            onClick={onGoBack}
            data-testid="edit-details"
          >
            {t("Edit Details")}
          </Button>
          <Button
            variant="primary"
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              await onProceed(data);
              setIsSubmitting(false);
            }}
            data-testid="confirm-rec-action-request"
          >
            {t("Confirm {{action}} Request", { action: startCase(buttonActionText ?? action) })}
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}
